import { LegacyJournalId, TrackableType } from "@qendo/trackables";
import { type OptionData, OptionType } from "@qendo/types";
import { useGlobalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { BasicGallery } from "~/components/layout/gallery/BasicGallery";
import { ScaleOutput } from "~/components/layout/journal/ScaleOutput";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Subtitle, Title } from "~/components/layout/text";
import { Paragraphs } from "~/components/layout/text/Paragraphs";
import { Authorize } from "~/components/permission/Authorize";
import { Dismiss } from "~/components/permission/Dismiss";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useReport } from "~/providers/ReportProvider";
import { useTrackable } from "~/providers/TrackableProvider";
import { filled } from "~/util";
import { formatDateTimeForHumans } from "~/util/dates";
import { extractMeasures } from "~/util/qendo/measure";
import { isReportable } from "~/util/qendo/reports";

export default function JournalDetails() {
    const { id }: { id: string } = useGlobalSearchParams();
    const { handleDismiss } = useDismiss();
    const { findTrackable } = useTrackable();
    const { findOption } = useOptions();
    const { findJournal, destroyJournal, canUpdateJournal } = useJournal();
    const [confirm, setConfirm] = useState(false);
    const { addToast, saving, setSaving } = useFeedback();
    const { can, cycle, refreshCycle } = useProfile();
    const { refreshReports } = useReport();

    const journal = useMemo(() => findJournal(id), [id, findJournal]);
    // biome-ignore lint/correctness/useExhaustiveDependencies(findTrackable): API
    const trackable = useMemo(() => findTrackable(journal?.trackable_id), [journal]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(findOption): API
    const measurements = useMemo(() => extractMeasures(findOption, trackable, journal), [trackable, journal]);
    const treatments = useMemo(
        () =>
            journal?.treatment_ids
                ?.map((id) => findOption(OptionType.symptoms_treatments, id))
                ?.filter((v): v is OptionData => Boolean(v))
                ?.map(({ title }) => title)
                ?.join(", "),
        [findOption, journal?.treatment_ids],
    );

    const can_update = Boolean(journal && trackable && canUpdateJournal(trackable.type));
    const can_delete = Boolean(can_update && !saving);
    const is_period = trackable?.legacy_id === LegacyJournalId.period;

    const doDelete = () => {
        if (!can_delete || !journal || !trackable) {
            return;
        }
        const should_refresh_cycle = is_period && cycle.tracked && can("view", "cycle");
        const should_refresh_reports = isReportable(trackable);

        setSaving(true);

        destroyJournal({ type: trackable.type, id: journal.id, recorded_on: journal.recorded_on })
            .then(() => {
                should_refresh_cycle && refreshCycle();
                should_refresh_reports && refreshReports();
            })
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Journal deleted",
                    duration: 3000,
                });
            })
            .finally(() => setSaving(false));
    };

    if (!journal || !trackable) {
        return <Dismiss />;
    }

    return (
        <Authorize ability="view" any section={["activities", "journal", "symptoms"]}>
            <FullPageModal
                asRoute
                title="Log details"
                headerLeft={() => (
                    <Only when={can_update}>
                        <Button
                            variant="quiet"
                            title="Edit"
                            href={{
                                pathname: is_period ? "/periods/[id]/edit" : "/journal/[id]/edit",
                                params: { id },
                            }}
                            size="sm"
                        />
                    </Only>
                )}
                withDone
                onRequestClose={handleDismiss}
                footer={() => (
                    <Only when={can_update}>
                        <Button
                            icon="trash"
                            title="Delete log"
                            disabled={!can_delete}
                            variant="destructive"
                            onPress={() => setConfirm(true)}
                        />
                        <Only
                            when={confirm}
                            show={() => (
                                <ConfirmationModal
                                    onConfirm={doDelete}
                                    title="Are you sure you want to delete this log?"
                                    confirmText="Delete log"
                                    confirmVariant="destructive-quiet"
                                    loading={filled(saving)}
                                    onRequestClose={() => setConfirm(false)}
                                />
                            )}
                        />
                    </Only>
                )}
            >
                <View style={[Padding.top_md, Gap.lg, Flex.flex, Items.stretch]}>
                    <View style={[Items.start, Gap.xs]}>
                        <Only when={journal.flare}>
                            <Badge title="Triggered a flare" icon="zap-filled" variant="danger" size="sm" />
                        </Only>
                        <Title size="xl">{trackable.title}</Title>
                        <Subtitle>{formatDateTimeForHumans(journal.recorded_at, "long")}</Subtitle>
                        <Only when={journal.flag && trackable.type === TrackableType.symptom}>
                            <Badge
                                title="Prevented usual activities"
                                icon="warning-outline"
                                variant="warning"
                                size="sm"
                            />
                        </Only>
                    </View>
                    <DataList>
                        {measurements.map(({ title, value, key }) => (
                            <DataListItem title={title} data={value} key={key} />
                        ))}
                        <ScaleOutput
                            scale={trackable.scale}
                            label={trackable.scale_name}
                            value={journal.scale}
                            asDataList
                        />
                        {filled(treatments) && <DataListItem title="Treatments" data={treatments} />}
                    </DataList>
                    <Only when={journal.description}>
                        <DataList>
                            <DataListItem title="Description" data={<Paragraphs content={journal.description} />} />
                        </DataList>
                    </Only>
                    <Only when={journal.photograph_ids}>
                        <BasicGallery images={journal.photograph_ids} />
                    </Only>
                </View>
            </FullPageModal>
        </Authorize>
    );
}
